import React, { useState, useEffect, useCallback } from 'react';
import { apiCall } from '../../utils/api';
import { toast } from '../ui/sonner';
import PasswordConfirmModal from '../PasswordConfirmModal';
import {
  Briefcase, RefreshCw, Search, Archive, ArchiveRestore, Trash2,
  AlertCircle, ChevronLeft, ChevronRight,
} from 'lucide-react';

const STATUS_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'active', label: 'Active' },
  { id: 'assigned', label: 'Assigned' },
  { id: 'in_progress', label: 'In Progress' },
  { id: 'pending_approval', label: 'Pending Approval' },
  { id: 'completed', label: 'Completed' },
  { id: 'cancelled', label: 'Cancelled' },
  { id: 'archived', label: 'Archived' },
];

const STATUS_PILL = {
  active: 'bg-blue-50 text-blue-700 border-blue-200',
  assigned: 'bg-amber-50 text-amber-700 border-amber-200',
  in_progress: 'bg-violet-50 text-violet-700 border-violet-200',
  pending_approval: 'bg-orange-50 text-orange-700 border-orange-200',
  completed: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  cancelled: 'bg-slate-100 text-slate-700 border-slate-200',
  archived: 'bg-amber-50 text-amber-700 border-amber-200',
};

const PAGE_SIZE = 25;

export default function AdminJobsPanel() {
  const [jobs, setJobs] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useState(0);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');

  const [actionModal, setActionModal] = useState(null); // { mode: 'archive'|'delete'|'bulk-archive'|'bulk-delete', job?, jobIds? }
  const [actionBusy, setActionBusy] = useState(false);
  const [actionError, setActionError] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status && status !== 'all') params.set('status', status);
      if (q) params.set('q', q);
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      const res = await apiCall(`/api/admin/jobs?${params.toString()}`);
      setJobs(res.jobs || []);
      setTotal(res.total || 0);
    } catch (e) {
      toast.error(e?.message || 'Failed to load jobs');
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, [status, q, skip]);

  useEffect(() => { load(); }, [load]);

  // Clear selection whenever the visible list changes meaningfully
  useEffect(() => { setSelectedIds(new Set()); }, [status, q, skip]);

  const toggleSelected = (jobId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const toggleSelectAll = () => {
    const ids = jobs.map((j) => j._id);
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.has(id));
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(ids));
  };

  const bulkRestore = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    try {
      const res = await apiCall('/api/jobs/bulk-restore', {
        method: 'POST',
        body: JSON.stringify({ job_ids: ids }),
      });
      const skipped = (res.skipped || []).length;
      toast.success(`Restored ${res.restored || 0} job${(res.restored||0)!==1?'s':''}${skipped ? ` (${skipped} skipped)` : ''}`);
      setSelectedIds(new Set());
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to restore');
    }
  };

  const onSearch = (e) => {
    e?.preventDefault?.();
    setSkip(0);
    setQ(qInput.trim());
  };

  const submitAction = async ({ password, reason }) => {
    if (!actionModal) return;
    setActionBusy(true);
    setActionError('');
    const { mode, job, jobIds } = actionModal;
    try {
      if (mode === 'archive') {
        await apiCall(`/api/jobs/${job._id}/archive`, {
          method: 'POST',
          body: JSON.stringify({ password, reason }),
        });
        toast.success('Job archived');
      } else if (mode === 'delete') {
        await apiCall(`/api/jobs/${job._id}`, {
          method: 'DELETE',
          body: JSON.stringify({ password, reason }),
        });
        toast.success('Job permanently deleted');
      } else if (mode === 'bulk-archive') {
        const res = await apiCall('/api/jobs/bulk-archive', {
          method: 'POST',
          body: JSON.stringify({ job_ids: jobIds, password, reason }),
        });
        const skipped = (res.skipped || []).length;
        toast.success(`Archived ${res.archived || 0} job${(res.archived||0)!==1?'s':''}${skipped ? ` (${skipped} skipped)` : ''}`);
        setSelectedIds(new Set());
      } else if (mode === 'bulk-delete') {
        const res = await apiCall('/api/jobs/bulk-delete', {
          method: 'POST',
          body: JSON.stringify({ job_ids: jobIds, password, reason }),
        });
        const skipped = (res.skipped || []).length;
        toast.success(`Deleted ${res.deleted || 0} job${(res.deleted||0)!==1?'s':''}${skipped ? ` (${skipped} skipped)` : ''}`);
        setSelectedIds(new Set());
      }
      setActionModal(null);
      await load();
    } catch (e) {
      setActionError(e?.message || 'Action failed');
    } finally {
      setActionBusy(false);
    }
  };

  const restore = async (job) => {
    try {
      await apiCall(`/api/jobs/${job._id}/restore`, { method: 'POST' });
      toast.success('Job restored');
      await load();
    } catch (e) {
      toast.error(e?.message || 'Failed to restore');
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  return (
    <div className="space-y-5" data-testid="admin-jobs-panel">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Briefcase className="w-6 h-6 text-blue-600" />
            All Jobs / Shifts
          </h2>
          <p className="text-slate-500 mt-1">
            Global view across every employer & agent. Archive (recoverable) or delete (irreversible) — password required.
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-2 px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-slate-700 font-medium hover:bg-slate-50 transition"
          data-testid="admin-jobs-refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Status pills */}
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((s) => (
          <button
            key={s.id}
            onClick={() => { setStatus(s.id); setSkip(0); }}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
              status === s.id
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`}
            data-testid={`admin-jobs-filter-${s.id}`}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <form onSubmit={onSearch} className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={qInput}
            onChange={(e) => setQInput(e.target.value)}
            placeholder="Search by title or employer name…"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-lg text-sm bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-400"
            data-testid="admin-jobs-search-input"
          />
        </div>
        <button
          type="submit"
          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-semibold hover:bg-blue-700 transition"
          data-testid="admin-jobs-search-btn"
        >
          Search
        </button>
      </form>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="sticky top-2 z-30 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-xl px-4 py-2.5 flex items-center gap-3 flex-wrap"
          data-testid="admin-jobs-bulk-action-bar"
        >
          <span className="font-bold text-sm">{selectedIds.size} selected</span>
          <span className="text-blue-100 text-xs">•</span>
          <button
            onClick={() => { setActionError(''); setActionModal({ mode: 'bulk-archive', jobIds: Array.from(selectedIds) }); }}
            disabled={actionBusy}
            className="px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition disabled:opacity-50"
            data-testid="admin-jobs-bulk-archive-btn"
          >
            <Archive className="w-3.5 h-3.5" /> Archive
          </button>
          <button
            onClick={bulkRestore}
            disabled={actionBusy}
            className="px-3 py-1.5 bg-white/15 hover:bg-white/25 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition disabled:opacity-50"
            data-testid="admin-jobs-bulk-restore-btn"
          >
            <ArchiveRestore className="w-3.5 h-3.5" /> Restore
          </button>
          <button
            onClick={() => { setActionError(''); setActionModal({ mode: 'bulk-delete', jobIds: Array.from(selectedIds) }); }}
            disabled={actionBusy}
            className="px-3 py-1.5 bg-red-500/90 hover:bg-red-500 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5 transition disabled:opacity-50"
            data-testid="admin-jobs-bulk-delete-btn"
          >
            <Trash2 className="w-3.5 h-3.5" /> Delete
          </button>
          <button
            onClick={clearSelection}
            disabled={actionBusy}
            className="ml-auto px-2.5 py-1 bg-white/10 hover:bg-white/20 text-white text-xs rounded transition"
            data-testid="admin-jobs-bulk-clear-btn"
          >
            Clear
          </button>
        </div>
      )}

      {/* Jobs list */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
              <tr>
                <th className="px-3 py-3 w-10 text-left">
                  <input
                    type="checkbox"
                    checked={jobs.length > 0 && jobs.every((j) => selectedIds.has(j._id))}
                    ref={(el) => {
                      if (el) el.indeterminate = selectedIds.size > 0 && !jobs.every((j) => selectedIds.has(j._id));
                    }}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                    data-testid="admin-jobs-select-all"
                    aria-label="Select all"
                  />
                </th>
                <th className="text-left px-4 py-3 font-semibold">Title</th>
                <th className="text-left px-4 py-3 font-semibold">Employer</th>
                <th className="text-left px-4 py-3 font-semibold">Pay</th>
                <th className="text-left px-4 py-3 font-semibold">Status</th>
                <th className="text-left px-4 py-3 font-semibold">Created</th>
                <th className="text-right px-4 py-3 font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && jobs.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">Loading…</td></tr>
              )}
              {!loading && jobs.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-slate-500">
                    <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
                    No jobs match the current filters.
                  </td>
                </tr>
              )}
              {jobs.map((job) => {
                const isArchived = job.status === 'archived' || job.is_archived;
                const checked = selectedIds.has(job._id);
                return (
                  <tr key={job._id} className={`transition ${checked ? 'bg-blue-50/40' : 'hover:bg-slate-50'}`} data-testid={`admin-job-row-${job._id}`}>
                    <td className="px-3 py-3">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleSelected(job._id)}
                        className="w-4 h-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                        data-testid={`admin-job-select-${job._id}`}
                        aria-label={`Select ${job.title}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900 truncate max-w-[260px]">{job.title || '—'}</div>
                      <div className="text-xs text-slate-400 truncate max-w-[260px]">{job.industry || ''} {job.category ? `· ${job.category}` : ''}</div>
                    </td>
                    <td className="px-4 py-3">
                      <div className="text-slate-800 truncate max-w-[200px]">{job.employer_name || '—'}</div>
                      <div className="text-xs text-slate-400 truncate max-w-[200px]">{job.employer_id || ''}</div>
                    </td>
                    <td className="px-4 py-3 text-slate-700 whitespace-nowrap">
                      ${Number(job.pay_rate || 0).toFixed(2)}<span className="text-xs text-slate-400">/hr</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_PILL[job.status] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                        {job.status || '—'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                      {job.created_at ? new Date(job.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {isArchived ? (
                          <button
                            onClick={() => restore(job)}
                            className="px-2.5 py-1.5 bg-slate-700 text-white rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-slate-800 transition"
                            data-testid={`admin-job-restore-${job._id}`}
                          >
                            <ArchiveRestore className="w-3.5 h-3.5" /> Restore
                          </button>
                        ) : (
                          <button
                            onClick={() => { setActionError(''); setActionModal({ mode: 'archive', job }); }}
                            className="px-2.5 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-amber-100 transition"
                            data-testid={`admin-job-archive-${job._id}`}
                          >
                            <Archive className="w-3.5 h-3.5" /> Archive
                          </button>
                        )}
                        <button
                          onClick={() => { setActionError(''); setActionModal({ mode: 'delete', job }); }}
                          className="px-2.5 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-md text-xs font-semibold flex items-center gap-1 hover:bg-red-100 transition"
                          data-testid={`admin-job-delete-${job._id}`}
                        >
                          <Trash2 className="w-3.5 h-3.5" /> Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 bg-slate-50 text-sm">
          <span className="text-slate-600">
            {total === 0 ? '0' : `${skip + 1}–${Math.min(skip + jobs.length, total)}`} of {total.toLocaleString()}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))}
              disabled={skip === 0 || loading}
              className="px-2 py-1.5 rounded-lg text-slate-600 hover:bg-slate-200 disabled:opacity-40"
              data-testid="admin-jobs-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="px-2 text-slate-700 font-medium">{currentPage} / {totalPages}</span>
            <button
              onClick={() => setSkip(skip + PAGE_SIZE)}
              disabled={skip + PAGE_SIZE >= total || loading}
              className="px-2 py-1.5 rounded-lg text-slate-600 hover:bg-slate-200 disabled:opacity-40"
              data-testid="admin-jobs-next"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <PasswordConfirmModal
        open={!!actionModal}
        onClose={() => { if (!actionBusy) { setActionModal(null); setActionError(''); } }}
        onConfirm={submitAction}
        busy={actionBusy}
        error={actionError}
        requireReason
        tone={(actionModal?.mode === 'delete' || actionModal?.mode === 'bulk-delete') ? 'danger' : 'warning'}
        title={
          actionModal?.mode === 'bulk-delete'
            ? `Permanently delete ${actionModal?.jobIds?.length || 0} job${(actionModal?.jobIds?.length || 0) !== 1 ? 's' : ''}?`
          : actionModal?.mode === 'bulk-archive'
            ? `Archive ${actionModal?.jobIds?.length || 0} job${(actionModal?.jobIds?.length || 0) !== 1 ? 's' : ''}?`
          : actionModal?.mode === 'delete'
            ? `Permanently delete "${actionModal?.job?.title || 'job'}"?`
            : `Archive "${actionModal?.job?.title || 'job'}"?`
        }
        description={
          (actionModal?.mode === 'delete' || actionModal?.mode === 'bulk-delete')
            ? 'This will erase the selected job(s) and all related applications, invites, attendance and chat data across the platform. This cannot be undone.'
            : 'Selected job(s) will be hidden from workers and pending applications/invites will be cancelled. You can restore them later.'
        }
        confirmLabel={
          actionModal?.mode === 'bulk-delete' ? `Delete ${actionModal?.jobIds?.length || 0} Permanently`
          : actionModal?.mode === 'bulk-archive' ? `Archive ${actionModal?.jobIds?.length || 0} Job${(actionModal?.jobIds?.length || 0) !== 1 ? 's' : ''}`
          : actionModal?.mode === 'delete' ? 'Delete Permanently'
          : 'Archive Job'
        }
      />
    </div>
  );
}
